// src/lib/CurrencyContext.js
//
// Reactive currency context. Wraps the app once, exposes a useCurrency()
// hook that returns formatters that re-render their consumers whenever
// the user changes their currency in Settings.
//
// Usage in components:
//   const { formatCurrency, formatCurrencyPrecise, currencyCode } = useCurrency()
//
// Usage in Settings to change currency:
//   const { setCurrency } = useCurrency()
//   await setCurrency('EUR')
//
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from './supabase'

const CURRENCY_MAP = {
  'USD': { code: 'USD', symbol: '$', locale: 'en-US' },
  'ILS': { code: 'ILS', symbol: '₪', locale: 'he-IL' },
  'EUR': { code: 'EUR', symbol: '€', locale: 'de-DE' },
  'GBP': { code: 'GBP', symbol: '£', locale: 'en-GB' },
  'MXN': { code: 'MXN', symbol: '$', locale: 'es-MX' },
}

const DEFAULT = CURRENCY_MAP.USD

// Mutable module state — synced from React context.
// Use this in non-React helpers (e.g. Plan.js's formatMoney).
let _currentCurrency = DEFAULT

export const getCurrentCurrency = () => _currentCurrency

const CurrencyContext = createContext({
  currencyCode: 'USD',
  currencySymbol: '$',
  currencyLocale: 'en-US',
  setCurrency: async () => {},
  formatCurrency: (amount) => `$${(amount || 0).toFixed(0)}`,
  formatCurrencyPrecise: (amount) => `$${(amount || 0).toFixed(2)}`,
  loaded: false,
})

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(DEFAULT)
  const [loaded, setLoaded] = useState(false)

  // On mount, hydrate currency from the user's profile
  useEffect(() => {
    let mounted = true
    const hydrate = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        if (mounted) setLoaded(true)
        return
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('currency_code, currency_locale, currency_symbol')
        .eq('id', user.id)
        .single()
      if (profile && mounted) {
        const code = profile.currency_code || 'USD'
        const fromMap = CURRENCY_MAP[code] || DEFAULT
        const synced = {
          code: fromMap.code,
          symbol: profile.currency_symbol || fromMap.symbol,
          locale: profile.currency_locale || fromMap.locale,
        }
        _currentCurrency = synced  // Sync module state on hydrate
        setCurrencyState(synced)
      }
      if (mounted) setLoaded(true)
    }
    hydrate()
    return () => { mounted = false }
  }, [])

  // Persist to DB and update state
  const setCurrency = useCallback(async (code) => {
    const next = CURRENCY_MAP[code] || DEFAULT
    _currentCurrency = next  // Sync module state
    setCurrencyState(next)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    await supabase
      .from('profiles')
      .update({
        currency_code: next.code,
        currency_symbol: next.symbol,
        currency_locale: next.locale,
      })
      .eq('id', user.id)
  }, [])

  // Formatters — re-created when currency changes, which forces consumers
  // to re-render and produce fresh-formatted values.
  const formatCurrency = useCallback((amount) => {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      maximumFractionDigits: 0,
    }).format(amount || 0)
  }, [currency])

  const formatCurrencyPrecise = useCallback((amount) => {
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0)
  }, [currency])

  return (
    <CurrencyContext.Provider
      value={{
        currencyCode: currency.code,
        currencySymbol: currency.symbol,
        currencyLocale: currency.locale,
        setCurrency,
        formatCurrency,
        formatCurrencyPrecise,
        loaded,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  )
}

export const useCurrency = () => useContext(CurrencyContext)

export const CURRENCIES = [
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'ILS', symbol: '₪', label: 'Israeli Shekel' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'MXN', symbol: '$', label: 'Mexican Peso' },
]
